import { YES_NO, applicantSection, feeInfo, otherAttachments, penaltyDeclaration, replySection } from "modules/ebiForms/common";
import {
  bankAccountField,
  citizenshipFields,
  familyIncomeAttachments,
  familyIncomeFields,
  personFields,
  personsField,
  socialProxyAttachment,
  socialRepresentationSection,
} from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C7. Wniosek o ustalenie prawa do świadczeń z funduszu alimentacyjnego — art. 15 ustawy z 7.09.2007 r. o pomocy osobom
 * uprawnionym do alimentów; rozporządzenie MRiPS z 7.07.2023 r. (Dz.U. poz. 1341). Układ i brzmienie według wzoru FA-1
 * MRPiPS (https://www.gov.pl/attachment/b5107216-6029-486f-9acd-2e03c370016d, pobrany 22.10.2026): okres świadczeniowy,
 * osoby uprawnione (szkoła), organ egzekucyjny, członkowie rodziny, składki zdrowotne, dochody (pkt 5.1–5.4), oświadczenia
 * części II, rachunek. Zamiast zaświadczenia komornika wnioskodawca może złożyć oświadczenie o bezskuteczności egzekucji
 * — pola według wzoru ZFA-06 (…/attachment/55b6ea4f-b3f9-4532-9343-4feae7ff0286, pobrany 22.10.2026) — a organ sam wystąpi
 * do organu egzekucyjnego o zaświadczenie (art. 15 ust. 5–6). Osoby uprawnione i członków rodziny wpisuje się na listach
 * (bez załącznika FA-1Z); oświadczenie o uczęszczaniu do szkoły jest częścią FA-1 (pole „szkoła” osoby uprawnionej).
 * Oświadczenie ZFA-03 — według FA-1 tylko przy dochodach nieopodatkowanych (katalog: „[zawsze]”) — do potwierdzenia.
 */
const COURT_CERTIFICATE: EbiCondition = { field: "egzekucja", equals: "zaswiadczenie" };
const STATEMENT: EbiCondition = { field: "egzekucja", equals: "oswiadczenie" };
const ABROAD: EbiCondition = { field: "egzekucja", equals: "zagranica" };

const C7: IEbiFormDefinition = {
  code: "C7",
  version: 1,
  title: "Wniosek o ustalenie prawa do świadczeń z funduszu alimentacyjnego",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Świadczenia z funduszu alimentacyjnego, gdy egzekucja alimentów zasądzonych od rodzica jest bezskuteczna (wzór FA-1).",
  keywords: ["fundusz alimentacyjny", "alimenty", "FA-1", "komornik", "bezskuteczna egzekucja", "świadczenia"],
  organ: "Wójt / Burmistrz / Prezydent Miasta jako organ właściwy wierzyciela — realizuje ośrodek pomocy społecznej lub centrum świadczeń",
  legalBasis:
    "art. 15 ustawy z dnia 7 września 2007 r. o pomocy osobom uprawnionym do alimentów; rozporządzenie Ministra Rodziny i Polityki Społecznej z dnia 7 lipca 2023 r. w sprawie sposobu i trybu postępowania w sprawach o przyznanie świadczeń z funduszu alimentacyjnego",
  request: "Wnoszę o ustalenie prawa do świadczeń z funduszu alimentacyjnego dla osób uprawnionych wskazanych we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (art. 2 ust. 1 pkt 1 lit. a ustawy o opłacie skarbowej — sprawy alimentacyjne).",
  sections: [
    applicantSection(),
    {
      code: "daneWnioskodawcy",
      title: "Okres świadczeniowy, obywatelstwo i stan cywilny",
      fields: [
        {
          code: "okresSwiadczeniowy",
          type: "text",
          label: "Wniosek dotyczy okresu świadczeniowego",
          required: true,
          width: "third",
          maxLength: 9,
          placeholder: "2026/2027",
          hint: "Okres świadczeniowy trwa od 1 października do 30 września następnego roku; wnioski na nowy okres przyjmowane są drogą elektroniczną od 1 lipca (art. 20 ust. 3 ustawy).",
        },
        ...citizenshipFields(true),
      ],
    },
    socialRepresentationSection(),
    {
      code: "osobyUprawnione",
      title: "Osoby uprawnione do alimentów",
      fields: [
        {
          code: "wnioskodawcaJest",
          type: "radio",
          label: "Wnioskodawca (a gdy działa pełnomocnik — mocodawca) jest",
          required: true,
          options: [
            { value: "rodzic", label: "przedstawiciel ustawowy (rodzic) osoby uprawnionej" },
            { value: "opiekunPrawny", label: "opiekun prawny osoby uprawnionej" },
            { value: "osobaUprawniona", label: "pełnoletnia osoba uprawniona do alimentów" },
          ],
        },
        personsField({
          code: "osobyUprawnione",
          label: "Składam wniosek o przyznanie świadczeń z funduszu alimentacyjnego na następujące osoby uprawnione do alimentów",
          itemLabel: "Osoba uprawniona",
          required: true,
          max: 10,
          hint: "Jeśli osobą uprawnioną jesteś Ty — wpisz siebie.",
          fields: [
            ...personFields().filter((f) => f.code !== "pokrewienstwo"),
            {
              code: "szkola",
              type: "text",
              label: "Szkoła lub szkoła wyższa, do której uczęszcza / będzie uczęszczała osoba uprawniona (rodzaj i adres)",
              maxLength: 300,
              hint: "Wypełnij, jeśli osoba ma ukończone 18 lat albo ukończy je w okresie świadczeniowym.",
            },
          ],
        }),
        {
          code: "naukaKolejnyRok",
          type: "radio",
          label: "Osoba uprawniona (osoby uprawnione) w kolejnym roku szkolnym lub akademickim",
          required: true,
          options: [
            { value: "tak", label: "będzie (będą) uczyć się w szkole lub w szkole wyższej" },
            { value: "nie", label: "nie będzie (nie będą) uczyć się w szkole lub w szkole wyższej" },
          ],
        },
        {
          code: "orzeczenieZnaczny",
          type: "radio",
          label: "Czy któraś z osób uprawnionych, która ukończyła 18 lat i nie uczy się, ma orzeczenie o znacznym stopniu niepełnosprawności?",
          required: true,
          defaultValue: "nie",
          options: YES_NO,
        },
      ],
    },
    {
      code: "egzekucjaAlimentow",
      title: "Egzekucja alimentów",
      fields: [
        {
          code: "egzekucja",
          type: "radio",
          label: "Bezskuteczność egzekucji alimentów",
          required: true,
          options: [
            { value: "zaswiadczenie", label: "dołączam zaświadczenie organu egzekucyjnego (komornika sądowego albo syndyka) potwierdzające bezskuteczność egzekucji" },
            {
              value: "oswiadczenie",
              label: "składam oświadczenie o bezskuteczności egzekucji — proszę organ o uzyskanie zaświadczenia od organu egzekucyjnego (art. 15 ust. 5 ustawy)",
            },
            {
              value: "zagranica",
              label: "dłużnik przebywa za granicą — dołączam informację sądu lub instytucji o podjęciu czynności egzekucyjnych za granicą albo o braku podstaw do ich podjęcia",
            },
          ],
        },
        { code: "organEgzekucyjny", type: "text", label: "Organ prowadzący egzekucję alimentów (np. komornik sądowy przy Sądzie Rejonowym w …)", required: true, visibleWhen: STATEMENT, maxLength: 300 },
        { code: "organEgzekucyjnyAdres", type: "text", label: "Adres organu prowadzącego egzekucję", required: true, visibleWhen: STATEMENT, maxLength: 300 },
        { code: "dluznikImie", type: "text", label: "Imię zobowiązanego do alimentów", required: true, visibleWhen: STATEMENT, width: "half", maxLength: 100 },
        { code: "dluznikNazwisko", type: "text", label: "Nazwisko zobowiązanego do alimentów", required: true, visibleWhen: STATEMENT, width: "half", maxLength: 100 },
        { code: "dluznikPesel", type: "pesel", label: "PESEL zobowiązanego (jeżeli jest znany)", visibleWhen: STATEMENT, width: "half" },
        { code: "dluznikAdres", type: "text", label: "Adres zamieszkania zobowiązanego (jeżeli jest znany)", visibleWhen: STATEMENT, maxLength: 300 },
        { code: "egzekucjaWszczeta", type: "date", label: "Data wszczęcia egzekucji", required: true, visibleWhen: STATEMENT, width: "half" },
        { code: "tytulData", type: "date", label: "Data tytułu wykonawczego (np. wyroku sądu)", required: true, visibleWhen: STATEMENT, width: "half" },
        { code: "tytulSygnatura", type: "text", label: "Sygnatura akt", required: true, visibleWhen: STATEMENT, width: "half", maxLength: 50 },
        { code: "alimentyKwota", type: "number", label: "Wysokość alimentów miesięcznie [zł]", required: true, visibleWhen: STATEMENT, width: "half", decimals: 2, min: 0 },
        {
          code: "oswiadczenieBezskutecznosc",
          type: "declaration",
          label:
            "Oświadczam, że w okresie ostatnich dwóch miesięcy egzekucja alimentów należnych od wskazanego zobowiązanego, przyznanych tytułem wykonawczym wskazanym powyżej, okazała się bezskuteczna (art. 2 pkt 2 ustawy).",
          required: true,
          visibleWhen: STATEMENT,
        },
      ],
    },
    {
      code: "rodzina",
      title: "Członkowie rodziny i dochody",
      fields: [
        personsField({
          code: "czlonkowieRodziny",
          label: "Członkowie rodziny (oprócz Ciebie)",
          itemLabel: "Członek rodziny",
          requiredWhen: { not: { field: "wnioskodawcaJest", equals: "osobaUprawniona" } },
          fields: personFields("Pokrewieństwo (relacja do osoby uprawnionej)"),
          hint: "Wpisz osoby uprawnione, Twojego małżonka albo drugiego rodzica dzieci (z wyjątkiem rodzica zobowiązanego do alimentów), pozostałe dzieci do ukończenia 25. roku życia pozostające na Twoim utrzymaniu oraz dziecko powyżej 25 lat otrzymujące świadczenia z funduszu albo z orzeczeniem o znacznym stopniu niepełnosprawności, jeśli przysługuje na nie świadczenie pielęgnacyjne, specjalny zasiłek opiekuńczy lub zasiłek dla opiekuna. Do rodziny nie zalicza się dziecka pod opieką opiekuna prawnego ani dziecka w związku małżeńskim. Jeśli jesteś pełnoletnią osobą uprawnioną i rodzinę tworzysz tylko Ty — usuń pustą pozycję.",
        }),
        ...familyIncomeFields({ period: "okres świadczeniowy", nonTaxedForm: "ZFA-03", farmForm: "ZFA-05" }),
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        feeInfo(
          "infoWarunki",
          "Świadczenia z funduszu alimentacyjnego przysługują osobie uprawnionej do alimentów od rodzica na podstawie tytułu wykonawczego pochodzącego lub zatwierdzonego przez sąd, jeżeli egzekucja jest bezskuteczna — do ukończenia 18 lat, a gdy osoba uczy się w szkole lub szkole wyższej — do ukończenia 25 lat, przy orzeczeniu o znacznym stopniu niepełnosprawności — bezterminowo; jeżeli dochód rodziny na osobę nie przekracza kryterium dochodowego (art. 2 pkt 11 i art. 9 ustawy).",
        ),
        {
          code: "oswiadczenieWnioskodawcy",
          type: "declaration",
          label:
            "Oświadczam, że: powyższe dane są prawdziwe; zapoznałam/zapoznałem się z warunkami uprawniającymi do świadczeń z funduszu alimentacyjnego; osoba uprawniona (osoby uprawnione) nie pozostaje w związku małżeńskim ani nie przebywa w pieczy zastępczej lub instytucji zapewniającej całodobowe utrzymanie (dom pomocy społecznej, młodzieżowy ośrodek wychowawczy, schronisko dla nieletnich, zakład poprawczy, areszt śledczy, zakład karny, szkoła wojskowa lub inna szkoła, jeżeli instytucje te zapewniają nieodpłatnie pełne utrzymanie); w przypadku rezygnacji z kontynuacji nauki zobowiązuję się niezwłocznie poinformować organ przyznający świadczenia; przekazałam/przekazałem organowi egzekucyjnemu wszelkie znane mi informacje niezbędne do prowadzenia postępowania egzekucyjnego przeciwko dłużnikowi.",
          required: true,
        },
        feeInfo(
          "infoZmiany",
          "W przypadku zmiany liczby członków rodziny, umieszczenia osoby uprawnionej w instytucji zapewniającej całodobowe utrzymanie albo w pieczy zastępczej, zawarcia przez nią związku małżeńskiego oraz innych zmian mających wpływ na prawo do świadczeń (zwłaszcza uzyskania dochodu, uchylenia obowiązku alimentacyjnego lub zmiany wysokości alimentów, otrzymania alimentów) należy niezwłocznie powiadomić organ — w przeciwnym razie powstaną nienależnie pobrane świadczenia do zwrotu z odsetkami.",
        ),
        bankAccountField(),
        penaltyDeclaration(),
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "tytulWykonawczy",
      label: "Odpis orzeczenia sądu zasądzającego alimenty, postanowienia o zabezpieczeniu albo ugody sądowej lub mediacyjnej zatwierdzonej przez sąd",
      hint: "Podlegające wykonaniu — kopia.",
      required: true,
      multiple: true,
    },
    {
      code: "zaswiadczenieKomornika",
      label: "Zaświadczenie organu egzekucyjnego o bezskuteczności egzekucji za ostatnie 2 miesiące, z kwotą wyegzekwowaną i datą wszczęcia egzekucji",
      hint: "Oryginał zaświadczenia (skan lub dokument elektroniczny).",
      visibleWhen: COURT_CERTIFICATE,
      required: true,
      multiple: true,
    },
    {
      code: "egzekucjaZagranica",
      label: "Informacja sądu lub instytucji o podjęciu czynności egzekucyjnych za granicą albo o braku podstaw do ich podjęcia",
      visibleWhen: ABROAD,
      required: true,
      multiple: true,
    },
    ...familyIncomeAttachments({ nonTaxedForm: "ZFA-03", farmForm: "ZFA-05" }),
    {
      code: "orzeczenieZnacznyStopien",
      label: "Orzeczenie o znacznym stopniu niepełnosprawności osoby uprawnionej",
      visibleWhen: { field: "orzeczenieZnaczny", equals: "tak" },
      required: true,
    },
    {
      code: "postanowienieOpiekun",
      label: "Orzeczenie sądu o ustanowieniu opiekuna prawnego",
      visibleWhen: { field: "wnioskodawcaJest", equals: "opiekunPrawny" },
      required: true,
    },
    socialProxyAttachment(),
    otherAttachments(),
  ],
};

export default C7;
